import React, { useEffect } from 'react';
import { useCMS } from '../../context/CMSContext';
import { Project, SEOSettingsData } from '../../types/cms';
import { MASTER_JOURNAL_ARTICLES } from '../../data/journalArticles';

/**
 * Utility function to parse raw HTML strings and inject executable elements into a target container.
 * Browsers do not execute <script> tags when set via innerHTML.
 * This function recreates <script> elements using document.createElement so inline scripts,
 * external scripts (GA4, GTM, Pixels), and JSON-LD schema markup run and register in DOM.
 */
function injectExecutableHTML(target: HTMLElement, rawHtml: string, containerId: string) {
  let container = document.getElementById(containerId);
  if (!container) {
    container = document.createElement('div');
    container.id = containerId;
    container.setAttribute('data-oltraya-injection', 'true');
    container.style.display = 'none';
    target.appendChild(container);
  } else {
    container.innerHTML = '';
  }

  if (!rawHtml || !rawHtml.trim()) {
    return;
  }

  const parser = new DOMParser();
  const parsedDoc = parser.parseFromString(`<!DOCTYPE html><html><head>${rawHtml}</head><body></body></html>`, 'text/html');
  const nodesToInject = Array.from(parsedDoc.head.childNodes);

  nodesToInject.forEach((node) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      if (el.tagName.toLowerCase() === 'script') {
        const newScript = document.createElement('script');
        Array.from(el.attributes).forEach((attr) => {
          newScript.setAttribute(attr.name, attr.value);
        });
        if (el.innerHTML) {
          newScript.text = el.innerHTML;
        }
        container?.appendChild(newScript);
      } else {
        container?.appendChild(el.cloneNode(true));
      }
    }
  });
}

/**
 * Helper to update or create a <meta> tag in document.head
 */
function setMetaTag(attrName: 'name' | 'property', attrValue: string, content: string) {
  if (!content && content !== '') return;
  let meta = document.head.querySelector(`meta[${attrName}="${attrValue}"]`) as HTMLMetaElement | null;
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute(attrName, attrValue);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
}

/**
 * Helper to update or create a <link rel="..."> tag in document.head
 */
function setLinkTag(rel: string, href: string) {
  if (!href) return;
  let link = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', rel);
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
}

/**
 * Helper to inject or update a JSON-LD structured data script
 */
function setJsonLdScript(scriptId: string, jsonContent: object | null) {
  let script = document.getElementById(scriptId) as HTMLScriptElement | null;
  if (!jsonContent) {
    if (script) script.remove();
    return;
  }

  if (!script) {
    script = document.createElement('script');
    script.id = scriptId;
    script.type = 'application/ld+json';
    document.head.appendChild(script);
  }
  script.text = JSON.stringify(jsonContent, null, 2);
}

export const SEORuntimeManager: React.FC = () => {
  const {
    data,
    activePublicPage,
    setActivePublicPage,
    selectedProject,
    selectedJournalSlug,
    selectedLeaderId
  } = useCMS();

  const seo = data.seoSettings;
  const site = data.siteSettings;
  const brandName = seo?.brandName || site?.brandName || 'OLTRAYA';
  const titleSeparator = seo?.titleSeparator || '|';
  const canonicalDomain = (seo?.canonicalDomain || 'https://oltraya.com').replace(/\/+$/, '');

  // -------------------------------------------------------------
  // 1. CLIENT-SIDE 301 / 302 REDIRECT ENGINE (TAB 6) & CANONICAL URL NORMALIZATION
  // -------------------------------------------------------------
  useEffect(() => {
    // Normalize browser history to clean non-trailing-slash URL if currently showing trailing slash
    if (typeof window !== 'undefined' && window.location.pathname.length > 1 && window.location.pathname.endsWith('/')) {
      const cleanPathname = window.location.pathname.replace(/\/+$/, '');
      window.history.replaceState(null, '', `${cleanPathname}${window.location.search}${window.location.hash}`);
    }

    const redirectRules = seo?.lifecycleAndRedirects?.redirectRules || [];
    if (redirectRules.length === 0) return;

    const currentPath = window.location.pathname.toLowerCase().replace(/\/+$/, '') || '/';
    const match = redirectRules.find(
      (r) => r.sourcePath && r.sourcePath.toLowerCase().replace(/\/+$/, '') === currentPath
    );

    if (match && match.targetPath && match.targetPath !== currentPath) {
      console.log(`[SEO 301 Engine]: Redirecting from ${currentPath} to ${match.targetPath} (${match.statusCode || 301})`);
      const target = match.targetPath.replace(/^\//, '') || 'home';
      window.history.replaceState(null, '', match.targetPath);
      setActivePublicPage(target);
    }
  }, [seo?.lifecycleAndRedirects?.redirectRules, setActivePublicPage]);

  // -------------------------------------------------------------
  // 2. METADATA, TITLE & OPEN GRAPH RUNTIME (TABS 1, 2, 5)
  // -------------------------------------------------------------
  useEffect(() => {
    // Determine active page override or dynamic template
    const pageOverride = seo?.pageOverrides?.find(
      (p) => p.pageId === activePublicPage || p.slug === `/${activePublicPage}` || p.slug === activePublicPage
    );

    let pageTitle = '';
    let pageDesc = '';
    let pageCanonical = '';
    let pageOgImage = pageOverride?.ogImageUrl || site?.logoImageUrl || '/oltraya-brand-logo.png';
    let currentPath = `/${activePublicPage === 'home' ? '' : activePublicPage}`;

    // Handle Project Detail dynamic template evaluation (Tab 2)
    if (activePublicPage === 'project-detail' && selectedProject) {
      const p = selectedProject;
      currentPath = `/projects/${p.slug || p.id || 'oltraya-verde'}`;
      pageOgImage = p.image || pageOgImage;

      const templateTitle = seo?.templates?.listingTitleTemplate || '{Title} | {Brand} - {Location}';
      const templateDesc = seo?.templates?.listingDescTemplate || '{Title} - {Type} in {Location}. {Area} starting from {Price}.';

      const replacer = (str: string) =>
        str
          .replace(/{Title}/g, p.title || 'Development')
          .replace(/{PropertyType}/g, p.type || 'Sanctuary Agro-Estates')
          .replace(/{Type}/g, p.type || 'Sanctuary Agro-Estates')
          .replace(/{Location}/g, p.location || 'Mumbai 3.0 Corridor')
          .replace(/{Area}/g, p.area || '9.1 Acres')
          .replace(/{Price}/g, p.startingPrice || '₹18 Lakhs+')
          .replace(/{Brand}/g, brandName)
          .replace(/{Category}/g, p.categoryTag || 'Current Project')
          .replace(/{Neighborhood}/g, p.location?.split(',')[0] || 'Khalapur')
          .replace(/{City}/g, 'Mumbai 3.0')
          .replace(/{Corridor}/g, p.corridor || 'NAINA Infrastructure Belt');

      pageTitle = replacer(templateTitle);
      pageDesc = replacer(templateDesc);
    } else if (activePublicPage === 'journal-detail') {
      const activeJournalSlug = selectedJournalSlug || (typeof window !== 'undefined' ? (
        window.location.pathname.startsWith('/journal/') ? window.location.pathname.replace(/^\/journal\//, '') :
        (window.location.pathname.startsWith('/journal-') && window.location.pathname !== '/journal-detail') ? window.location.pathname.replace(/^\/journal-/, '') :
        new URLSearchParams(window.location.search).get('slug')
      ) : null) || 'the-most-expensive-thing';

      const combinedArticles: any[] = [
        ...(data.journalPage?.articles || []),
        ...MASTER_JOURNAL_ARTICLES,
        ...(data.journal || []).map((j: any, idx: number) => ({
          slug: j.slug || (j.id === 'journal-1' ? 'why-mumbai-3' : j.id === 'journal-2' ? 'biophilic-architecture' : `journal-${j.id || idx}`),
          title: j.title,
          excerpt: j.excerpt,
          image: j.image,
          date: j.date
        }))
      ];
      const art = combinedArticles.find((a: any) => a.slug === activeJournalSlug || a.id === activeJournalSlug) || combinedArticles[0];
      if (art) {
        pageTitle = `${art.title} ${titleSeparator} ${brandName} Journal`;
        pageDesc = art.excerpt || art.description || seo?.defaultMetaDescription || '';
        pageOgImage = art.image || pageOgImage;
        currentPath = `/journal/${art.slug || art.id}`;
      }
    } else if (activePublicPage === 'leadership-detail') {
      const activeLeaderId = selectedLeaderId || (typeof window !== 'undefined' ? (
        window.location.pathname.toLowerCase().includes('chandni') ? 'chandni-rav' :
        window.location.pathname.toLowerCase().includes('ricky') ? 'ricky' : 'nitin'
      ) : 'nitin');

      const leaders = data.leadershipPage?.leaders || [];
      const ldr = leaders.find((l: any) => 
        l.id === activeLeaderId || 
        l.id.toLowerCase() === activeLeaderId?.toLowerCase() ||
        ((activeLeaderId === 'chandni-rav' || activeLeaderId === 'chandni') && (l.id.includes('chandni') || l.name.toLowerCase().includes('chandni'))) ||
        ((activeLeaderId === 'ricky' || activeLeaderId === 'ricky-yeole') && (l.id.includes('ricky') || l.name.toLowerCase().includes('ricky'))) ||
        ((activeLeaderId === 'nitin' || activeLeaderId === 'nitin-mudaliar') && (l.id.includes('nitin') || l.name.toLowerCase().includes('nitin')))
      ) || leaders[0];

      if (ldr) {
        pageTitle = `${ldr.name} ${titleSeparator} ${ldr.role} ${titleSeparator} ${brandName}`;
        pageDesc = ldr.detailPage?.heroIntro || ldr.excerpt?.[0] || `Biographical credentials and profile of ${ldr.name}, ${ldr.role} at Oltraya.`;
        pageOgImage = ldr.detailPage?.heroImage || ldr.image || pageOgImage;
        const slug = (ldr.id.includes('chandni') || ldr.name.toLowerCase().includes('chandni'))
          ? 'leadership-chandnirav'
          : (ldr.id.includes('ricky') || ldr.name.toLowerCase().includes('ricky'))
          ? 'leadership-rickyyeole'
          : 'leadership-nitinmudaliar';
        currentPath = `/${slug}`;
      }
    } else if (pageOverride && pageOverride.metaTitle) {
      pageTitle = pageOverride.metaTitle;
      pageDesc = pageOverride.metaDescription || seo?.defaultMetaDescription || '';
      pageCanonical = pageOverride.canonicalUrl || '';
    }

    // Default Fallbacks
    if (!pageTitle) {
      const defaultPageNames: Record<string, string> = {
        home: seo?.siteTitle || `${brandName} — Design-Led Sanctuary Agro-Estates & Boutique Villas`,
        projects: `Portfolio of Places & Masterplans ${titleSeparator} ${brandName}`,
        about: `About Our Foundation & Architectural Heritage ${titleSeparator} ${brandName}`,
        philosophy: `Design Philosophy & Topography ${titleSeparator} ${brandName}`,
        'why-oltraya': `Why ${brandName} — The Investment & Land Thesis ${titleSeparator} ${brandName}`,
        'investor-group': `Institutional Land Holdings & Growth ${titleSeparator} ${brandName}`,
        leadership: `Leadership & Advisory Board ${titleSeparator} ${brandName}`,
        'mumbai-3': `Mumbai 3.0 Infrastructure Corridor & NAINA ${titleSeparator} ${brandName}`,
        journal: `Editorial Journal & Perspectives ${titleSeparator} ${brandName}`,
        faqs: `Frequently Asked Questions & Ownership Protocols ${titleSeparator} ${brandName}`,
        connect: `Private Consultations & Inquiries ${titleSeparator} ${brandName}`,
        privacy: `Privacy Policy ${titleSeparator} ${brandName}`,
        terms: `Terms & Conditions ${titleSeparator} ${brandName}`
      };
      pageTitle = defaultPageNames[activePublicPage] || `${brandName} Real Estate`;
    }

    if (!pageDesc) {
      pageDesc = seo?.defaultMetaDescription || site?.tagline || 'Design-led real estate development in India. Thoughtfully crafted agro-estates and boutique villas.';
    }

    // Apply Document Title
    document.title = pageTitle;

    // Apply Meta Tags (Description, Keywords, Canonical, OpenGraph, Twitter)
    setMetaTag('name', 'description', pageDesc);
    setMetaTag('name', 'keywords', `${brandName}, luxury real estate, agro-estates, NAINA corridor, Mumbai 3.0, Khalapur plots, gated land parcel`);
    setLinkTag('canonical', pageCanonical || `${canonicalDomain}${currentPath}`);

    // Robots Indexation Tag (Tab 5)
    // Force noindex, nofollow on all *.vercel.app domains or if disabled in CMS
    const isVercelHost = typeof window !== 'undefined' && (
      window.location.hostname.endsWith('.vercel.app') ||
      window.location.hostname.includes('vercel.app')
    );
    const allowIndex = !isVercelHost && (seo?.robotsSettings?.defaultIndex !== false);
    const robotsContent = allowIndex
      ? 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
      : 'noindex, nofollow';
    setMetaTag('name', 'robots', robotsContent);

    // OpenGraph
    setMetaTag('property', 'og:title', pageTitle);
    setMetaTag('property', 'og:description', pageDesc);
    setMetaTag('property', 'og:url', `${canonicalDomain}${currentPath}`);
    const fullOgImage = pageOgImage.startsWith('/') ? `${canonicalDomain}${pageOgImage}` : pageOgImage;
    setMetaTag('property', 'og:image', fullOgImage);
    setMetaTag('property', 'og:site_name', brandName);
    setMetaTag('property', 'og:type', activePublicPage.includes('journal') ? 'article' : 'website');

    // Twitter
    setMetaTag('name', 'twitter:card', 'summary_large_image');
    setMetaTag('name', 'twitter:title', pageTitle);
    setMetaTag('name', 'twitter:description', pageDesc);
    setMetaTag('name', 'twitter:image', fullOgImage);
  }, [
    activePublicPage,
    selectedProject,
    selectedJournalSlug,
    selectedLeaderId,
    seo,
    site,
    data.hero?.bgImageUrl,
    brandName,
    titleSeparator,
    canonicalDomain
  ]);

  // -------------------------------------------------------------
  // 3. REAL ESTATE SCHEMA (NAP) & GRAPH GENERATION (TAB 3)
  // -------------------------------------------------------------
  useEffect(() => {
    const s = seo?.schemaSettings;
    if (!s) return;

    const schemaGraph: any[] = [
      {
        '@type': s.businessType || 'RealEstateAgent',
        '@id': `${canonicalDomain}/#organization`,
        name: s.businessName || brandName,
        legalName: s.legalName || brandName,
        url: canonicalDomain,
        logo: `${canonicalDomain}/favicon.ico`,
        telephone: s.telephone,
        email: s.email,
        priceRange: s.priceRange || '₹₹₹₹',
        currenciesAccepted: s.currenciesAccepted || 'INR',
        paymentAccepted: s.paymentAccepted || 'Bank Transfer, Cheque',
        address: {
          '@type': 'PostalAddress',
          streetAddress: s.streetAddress,
          addressLocality: s.city,
          addressRegion: s.state,
          postalCode: s.postalCode,
          addressCountry: s.country || 'IN'
        },
        geo: {
          '@type': 'GeoCoordinates',
          latitude: s.latitude,
          longitude: s.longitude
        },
        openingHoursSpecification: [
          {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
            opens: '09:00',
            closes: '18:00'
          }
        ]
      },
      {
        '@type': 'WebSite',
        '@id': `${canonicalDomain}/#website`,
        url: canonicalDomain,
        name: brandName,
        description: seo?.defaultMetaDescription,
        publisher: {
          '@id': `${canonicalDomain}/#organization`
        }
      }
    ];

    // If viewing a project detail page, attach RealEstateListing Schema
    if (activePublicPage === 'project-detail' && selectedProject) {
      const p = selectedProject;
      schemaGraph.push({
        '@type': 'RealEstateListing',
        '@id': `${canonicalDomain}/projects/${p.slug || p.id}#listing`,
        name: p.title,
        description: p.description || p.tagline,
        url: `${canonicalDomain}/projects/${p.slug || p.id}`,
        image: p.image,
        offers: {
          '@type': 'Offer',
          price: p.startingPrice?.replace(/[^0-9]/g, '') || '1800000',
          priceCurrency: 'INR',
          availability: 'https://schema.org/InStock',
          validFrom: new Date().toISOString().split('T')[0]
        },
        about: {
          '@type': 'Place',
          name: p.title,
          address: {
            '@type': 'PostalAddress',
            addressLocality: p.location || 'Khalapur',
            addressRegion: 'Maharashtra',
            addressCountry: 'IN'
          }
        }
      });
    }

    // If viewing a dedicated leadership profile, attach Person Schema
    if (activePublicPage === 'leadership-detail') {
      const activeLeaderId = selectedLeaderId || (typeof window !== 'undefined' ? (
        window.location.pathname.toLowerCase().includes('chandni') ? 'chandni-rav' :
        window.location.pathname.toLowerCase().includes('ricky') ? 'ricky' : 'nitin'
      ) : 'nitin');

      const leaders = data.leadershipPage?.leaders || [];
      const ldr = leaders.find((l: any) => 
        l.id === activeLeaderId || 
        l.id.toLowerCase() === activeLeaderId?.toLowerCase() ||
        ((activeLeaderId === 'chandni-rav' || activeLeaderId === 'chandni') && (l.id.includes('chandni') || l.name.toLowerCase().includes('chandni'))) ||
        ((activeLeaderId === 'ricky' || activeLeaderId === 'ricky-yeole') && (l.id.includes('ricky') || l.name.toLowerCase().includes('ricky'))) ||
        ((activeLeaderId === 'nitin' || activeLeaderId === 'nitin-mudaliar') && (l.id.includes('nitin') || l.name.toLowerCase().includes('nitin')))
      ) || leaders[0];

      if (ldr) {
        const vanitySlug = (ldr.id.includes('chandni') || ldr.name.toLowerCase().includes('chandni'))
          ? 'leadership-chandnirav'
          : (ldr.id.includes('ricky') || ldr.name.toLowerCase().includes('ricky'))
          ? 'leadership-rickyyeole'
          : 'leadership-nitinmudaliar';

        schemaGraph.push({
          '@type': 'Person',
          '@id': `${canonicalDomain}/${vanitySlug}#person`,
          name: ldr.name,
          jobTitle: ldr.role,
          description: ldr.detailPage?.heroIntro || ldr.excerpt?.[0] || `Biographical profile of ${ldr.name}, ${ldr.role} at Oltraya.`,
          image: ldr.detailPage?.heroImage || ldr.image,
          url: `${canonicalDomain}/${vanitySlug}`,
          worksFor: {
            '@id': `${canonicalDomain}/#organization`
          }
        });
      }
    }

    // If viewing a journal editorial article, attach BlogPosting Schema
    if (activePublicPage === 'journal-detail') {
      const activeJournalSlug = selectedJournalSlug || (typeof window !== 'undefined' ? (
        window.location.pathname.startsWith('/journal/') ? window.location.pathname.replace(/^\/journal\//, '') :
        (window.location.pathname.startsWith('/journal-') && window.location.pathname !== '/journal-detail') ? window.location.pathname.replace(/^\/journal-/, '') :
        new URLSearchParams(window.location.search).get('slug')
      ) : null) || 'the-most-expensive-thing';

      const combinedArticles: any[] = [
        ...(data.journalPage?.articles || []),
        ...MASTER_JOURNAL_ARTICLES,
        ...(data.journal || []).map((j: any, idx: number) => ({
          slug: j.slug || (j.id === 'journal-1' ? 'why-mumbai-3' : j.id === 'journal-2' ? 'biophilic-architecture' : `journal-${j.id || idx}`),
          title: j.title,
          excerpt: j.excerpt,
          image: j.image,
          date: j.date
        }))
      ];
      const art = combinedArticles.find((a: any) => a.slug === activeJournalSlug || a.id === activeJournalSlug) || combinedArticles[0];
      if (art) {
        schemaGraph.push({
          '@type': 'BlogPosting',
          '@id': `${canonicalDomain}/journal/${art.slug || art.id}#article`,
          headline: art.title,
          image: art.image,
          datePublished: art.date || new Date().toISOString(),
          description: art.excerpt || art.description || seo?.defaultMetaDescription,
          publisher: {
            '@id': `${canonicalDomain}/#organization`
          }
        });
      }
    }

    const fullSchemaObj = {
      '@context': 'https://schema.org',
      '@graph': schemaGraph
    };

    setJsonLdScript('oltraya-realestate-schema', fullSchemaObj);

    return () => {
      setJsonLdScript('oltraya-realestate-schema', null);
    };
  }, [seo?.schemaSettings, activePublicPage, selectedProject, selectedLeaderId, selectedJournalSlug, data.leadershipPage?.leaders, data.journalPage?.articles, canonicalDomain, brandName]);

  // -------------------------------------------------------------
  // 4. HYPER-LOCAL POI SCHEMA (TAB 4)
  // -------------------------------------------------------------
  useEffect(() => {
    const poi = seo?.hyperLocal;
    if (!poi || !poi.poiList || poi.poiList.length === 0) {
      setJsonLdScript('oltraya-poi-schema', null);
      return;
    }

    const poiSchema = {
      '@context': 'https://schema.org',
      '@type': 'Place',
      '@id': `${canonicalDomain}/#corridor-poi`,
      name: `${poi.regionName || 'Mumbai 3.0'} — ${poi.corridorName || 'NAINA Belt'}`,
      description: `Key landmarks, infrastructure transit, and connectivity hubs surrounding the ${brandName} developments.`,
      containsPlace: poi.poiList.map((p) => ({
        '@type': 'LandmarksOrHistoricalBuildings',
        name: p.name,
        category: p.category,
        additionalProperty: [
          {
            '@type': 'PropertyValue',
            name: 'Distance in Time',
            value: p.distanceTime
          },
          {
            '@type': 'PropertyValue',
            name: 'Distance in Kilometers',
            value: p.distanceKm
          }
        ]
      }))
    };

    setJsonLdScript('oltraya-poi-schema', poiSchema);

    return () => {
      setJsonLdScript('oltraya-poi-schema', null);
    };
  }, [seo?.hyperLocal, canonicalDomain, brandName]);

  // -------------------------------------------------------------
  // 5. FAQ SCHEMA GENERATOR (TAB 10)
  // -------------------------------------------------------------
  useEffect(() => {
    const isFaqPage = activePublicPage === 'faqs';
    const isAutoFaqEnabled = seo?.faqSchema?.enableAutoFaqSchema !== false;

    if (!isFaqPage || !isAutoFaqEnabled) {
      setJsonLdScript('oltraya-faq-schema', null);
      return;
    }

    const tiers = data.faqsPage?.tiers || [];
    const allQuestions: any[] = [];

    tiers.forEach((tier) => {
      (tier.items || []).forEach((item) => {
        if (item.question && item.answer) {
          let cleanAnswer = item.answer;
          if (seo?.faqSchema?.stripInlineHtmlStyles) {
            cleanAnswer = cleanAnswer.replace(/style="[^"]*"/gi, '').replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
          }
          allQuestions.push({
            '@type': 'Question',
            name: item.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: cleanAnswer
            }
          });
        }
      });
    });

    if (allQuestions.length > 0) {
      const faqSchemaObj = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        '@id': `${canonicalDomain}/faqs#faqpage`,
        mainEntity: allQuestions
      };
      setJsonLdScript('oltraya-faq-schema', faqSchemaObj);
    }

    return () => {
      setJsonLdScript('oltraya-faq-schema', null);
    };
  }, [
    activePublicPage,
    seo?.faqSchema,
    data.faqsPage?.tiers,
    canonicalDomain
  ]);

  // -------------------------------------------------------------
  // 6. BREADCRUMB SCHEMA (TAB 9)
  // -------------------------------------------------------------
  useEffect(() => {
    const isBreadcrumbEnabled = seo?.internalLinking?.enableBreadcrumbSchema !== false;
    if (!isBreadcrumbEnabled) {
      setJsonLdScript('oltraya-breadcrumb-schema', null);
      return;
    }

    const items = [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: canonicalDomain
      }
    ];

    if (activePublicPage !== 'home') {
      const pageNames: Record<string, string> = {
        projects: 'Projects',
        'project-detail': 'Project Details',
        about: 'About',
        philosophy: 'Philosophy',
        'why-oltraya': 'Why Oltraya',
        'investor-group': 'Investor Group',
        leadership: 'Leadership',
        'mumbai-3': 'Mumbai 3.0',
        journal: 'Journal',
        faqs: 'FAQs',
        connect: 'Connect',
        privacy: 'Privacy',
        terms: 'Terms'
      };

      if (activePublicPage === 'project-detail' && selectedProject) {
        items.push({
          '@type': 'ListItem',
          position: 2,
          name: 'Projects',
          item: `${canonicalDomain}/projects`
        });
        items.push({
          '@type': 'ListItem',
          position: 3,
          name: selectedProject.title,
          item: `${canonicalDomain}/projects/${selectedProject.slug || selectedProject.id}`
        });
      } else if (activePublicPage === 'journal-detail') {
        const activeJournalSlug = selectedJournalSlug || (typeof window !== 'undefined' ? (
          window.location.pathname.startsWith('/journal/') ? window.location.pathname.replace(/^\/journal\//, '') :
          (window.location.pathname.startsWith('/journal-') && window.location.pathname !== '/journal-detail') ? window.location.pathname.replace(/^\/journal-/, '') :
          new URLSearchParams(window.location.search).get('slug')
        ) : null) || 'the-most-expensive-thing';

        const combinedArticles: any[] = [
          ...(data.journalPage?.articles || []),
          ...MASTER_JOURNAL_ARTICLES,
          ...(data.journal || []).map((j: any, idx: number) => ({
            slug: j.slug || (j.id === 'journal-1' ? 'why-mumbai-3' : j.id === 'journal-2' ? 'biophilic-architecture' : `journal-${j.id || idx}`),
            title: j.title
          }))
        ];
        const art = combinedArticles.find((a: any) => a.slug === activeJournalSlug || a.id === activeJournalSlug) || combinedArticles[0];

        items.push({
          '@type': 'ListItem',
          position: 2,
          name: 'Journal',
          item: `${canonicalDomain}/journal`
        });
        items.push({
          '@type': 'ListItem',
          position: 3,
          name: art?.title || 'Article',
          item: `${canonicalDomain}/journal/${art?.slug || activeJournalSlug}`
        });
      } else {
        items.push({
          '@type': 'ListItem',
          position: 2,
          name: pageNames[activePublicPage] || activePublicPage,
          item: `${canonicalDomain}/${activePublicPage}`
        });
      }
    }

    const breadcrumbSchemaObj = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: items
    };

    setJsonLdScript('oltraya-breadcrumb-schema', breadcrumbSchemaObj);

    return () => {
      setJsonLdScript('oltraya-breadcrumb-schema', null);
    };
  }, [activePublicPage, selectedProject, seo?.internalLinking?.enableBreadcrumbSchema, canonicalDomain]);

  // -------------------------------------------------------------
  // 7. IMAGE ALT TEXT AUTOMATION (TAB 7)
  // -------------------------------------------------------------
  useEffect(() => {
    const altPattern = seo?.imageSEO?.dynamicAltPattern || '{Brand} - Luxury Real Estate & Architecture';
    const resolvedDefaultAlt = altPattern.replace(/{Brand}/g, brandName).replace(/{Location}/g, 'Mumbai 3.0');

    const timer = setTimeout(() => {
      const images = document.querySelectorAll('img');
      images.forEach((img) => {
        if (!img.getAttribute('alt') || img.getAttribute('alt')?.trim() === '') {
          img.setAttribute('alt', resolvedDefaultAlt);
        }
        // Improve Core Web Vitals (LCP, CLS, INP) for organic Google ranking
        if (!img.hasAttribute('loading') && !img.closest('header') && !img.closest('.hero-section')) {
          img.setAttribute('loading', 'lazy');
        }
        if (!img.hasAttribute('decoding')) {
          img.setAttribute('decoding', 'async');
        }
      });
    }, 300);

    return () => clearTimeout(timer);
  }, [activePublicPage, seo?.imageSEO?.dynamicAltPattern, brandName]);

  // -------------------------------------------------------------
  // 8. SITE-WIDE CODE INJECTION & PAGE CUSTOM MARKUP (TAB 11)
  // -------------------------------------------------------------
  useEffect(() => {
    const globalHeadCode = site?.headerCodeInjection || seo?.headerCodeInjection || '';
    injectExecutableHTML(document.head, globalHeadCode, 'oltraya-global-head-injection');

    return () => {
      const el = document.getElementById('oltraya-global-head-injection');
      if (el) el.remove();
    };
  }, [site?.headerCodeInjection, seo?.headerCodeInjection]);

  useEffect(() => {
    const globalBodyCode = site?.footerCodeInjection || seo?.footerCodeInjection || '';
    injectExecutableHTML(document.body, globalBodyCode, 'oltraya-global-body-injection');

    return () => {
      const el = document.getElementById('oltraya-global-body-injection');
      if (el) el.remove();
    };
  }, [site?.footerCodeInjection, seo?.footerCodeInjection]);

  // Page-specific custom head markup
  useEffect(() => {
    let pageCustomCode = '';

    const pageOverride = seo?.pageOverrides?.find(
      (p) => p.pageId === activePublicPage || p.slug === activePublicPage
    );
    if (pageOverride?.customHeadCode) {
      pageCustomCode = pageOverride.customHeadCode;
    }

    if (!pageCustomCode) {
      switch (activePublicPage) {
        case 'home':
          pageCustomCode = data.hero?.customHeadCode || '';
          break;
        case 'about':
          pageCustomCode = data.aboutPage?.customHeadCode || '';
          break;
        case 'philosophy':
          pageCustomCode = data.philosophyPage?.customHeadCode || '';
          break;
        case 'why-oltraya':
          pageCustomCode = data.whyOltrayaPage?.customHeadCode || '';
          break;
        case 'investor-group':
          pageCustomCode = data.investorGroupPage?.customHeadCode || '';
          break;
        case 'leadership':
          pageCustomCode = data.leadershipPage?.customHeadCode || '';
          break;
        case 'leadership-detail': {
          const activeLeaderId = selectedLeaderId || (typeof window !== 'undefined' ? (
            window.location.pathname.toLowerCase().includes('chandni') ? 'chandni-rav' :
            window.location.pathname.toLowerCase().includes('ricky') ? 'ricky' : 'nitin'
          ) : 'nitin');

          const leaders = data.leadershipPage?.leaders || [];
          const ldr = leaders.find((l: any) => 
            l.id === activeLeaderId || 
            l.id.toLowerCase() === activeLeaderId?.toLowerCase() ||
            ((activeLeaderId === 'chandni-rav' || activeLeaderId === 'chandni') && (l.id.includes('chandni') || l.name.toLowerCase().includes('chandni'))) ||
            ((activeLeaderId === 'ricky' || activeLeaderId === 'ricky-yeole') && (l.id.includes('ricky') || l.name.toLowerCase().includes('ricky'))) ||
            ((activeLeaderId === 'nitin' || activeLeaderId === 'nitin-mudaliar') && (l.id.includes('nitin') || l.name.toLowerCase().includes('nitin')))
          ) || leaders[0];

          pageCustomCode = ldr?.detailPage?.customHeadCode || (ldr as any)?.customHeadCode || data.leadershipPage?.customHeadCode || '';
          break;
        }
        case 'mumbai-3':
          pageCustomCode = data.mumbai3Page?.customHeadCode || '';
          break;
        case 'connect':
          pageCustomCode = data.connectPage?.customHeadCode || '';
          break;
        case 'faqs':
          pageCustomCode = data.faqsPage?.customHeadCode || '';
          break;
        case 'projects':
        case 'project-detail':
          pageCustomCode = data.projectsPage?.customHeadCode || '';
          break;
        case 'journal':
        case 'journal-detail':
          pageCustomCode = data.journalPage?.customHeadCode || '';
          break;
        case 'privacy':
          pageCustomCode = data.privacyPage?.customHeadCode || '';
          break;
        case 'terms':
          pageCustomCode = data.termsPage?.customHeadCode || '';
          break;
        default:
          pageCustomCode = '';
      }
    }

    injectExecutableHTML(document.head, pageCustomCode, 'oltraya-page-head-injection');

    return () => {
      const el = document.getElementById('oltraya-page-head-injection');
      if (el) el.remove();
    };
  }, [
    activePublicPage,
    selectedJournalSlug,
    selectedLeaderId,
    data.hero?.customHeadCode,
    data.aboutPage?.customHeadCode,
    data.philosophyPage?.customHeadCode,
    data.whyOltrayaPage?.customHeadCode,
    data.investorGroupPage?.customHeadCode,
    data.leadershipPage?.customHeadCode,
    data.leadershipPage?.leaders,
    data.mumbai3Page?.customHeadCode,
    data.connectPage?.customHeadCode,
    data.faqsPage?.customHeadCode,
    data.projectsPage?.customHeadCode,
    data.journalPage?.customHeadCode,
    data.privacyPage?.customHeadCode,
    data.termsPage?.customHeadCode,
    seo?.pageOverrides
  ]);

  return null;
};
